import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConnectionService } from './connection.service';
import { SwalService } from './swal.service';
import { environment } from '../../environments/environment';
import { foldPackagesIntoServices } from '../utility/packages-migration';
import { resolveDepositEuro } from '../utility/deposit';
import { emptyDiscount, eventTotals } from '../utility/totals';

@Injectable({ providedIn: 'root' })
export class EventService {
  constructor(
    private connection: ConnectionService,
    private swal: SwalService,
    private translate: TranslateService,
  ) {}

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  // ── CARICA EVENTO ─────────────────────────────────────────────────────────

  getMatrimonio(
    id: string,
    callback: (data: any) => void,
    errorCallback: () => void,
  ) {
    this.connection.get(
      `events/${id}`,
      (data: any) => {
        this.initDefaults(data);
        callback(data);
      },
      errorCallback,
    );
  }

  // ── SALVA EVENTO ──────────────────────────────────────────────────────────

  saveMatrimonio(id: string, data: any, callback?: (saved?: any) => void) {
    this.connection.put(
      `events/${id}`,
      data,
      (saved: any) => {
        this.swal.success('Salvato!');
        if (callback) callback(saved);
      },
      () => this.swal.errorDialog('Errore', 'Salvataggio fallito'),
    );
  }

  // ── VISIBILITÀ SEZIONI (override per evento) ──────────────────────────────
  updateVisibilityOverride(
    id: string,
    body: {
      collaborator_visibility_override: Record<string, boolean> | null;
      team_visibility_override: Record<string, boolean> | null;
    },
    onSuccess: () => void,
    onError: () => void,
  ): void {
    this.connection.put(`events/${id}/visibility`, body, onSuccess, onError);
  }

  // ── OVERRIDE CONTRATTO PER EVENTO ──────────────────────────────────────────

  saveContractOverride(
    id: string,
    override: Record<string, any> | null,
    onSuccess: () => void,
    onError: () => void,
  ): void {
    this.connection.put(
      `events/${id}/contract-override`,
      { override },
      onSuccess,
      onError,
    );
  }

  // ── GENERA E SALVA CONTRATTO ──────────────────────────────────────────────

  async buildContratto(
    eventId: string,
    onReady: (blob: Blob, ctx: { signed: boolean; lang: string }) => void,
  ) {
    const { value: lang } = await this.swal.select(
      this.t('swal.contract_lang_title'),
      { it: '🇮🇹 Italiano', en: '🇬🇧 English' },
      this.t('swal.generate'),
      this.t('swal.contract_lang_text'),
    );
    if (!lang) return;

    this.connection.get(
      `events/${eventId}`,
      (data: any) => {
        foldPackagesIntoServices(data);
        const errors: string[] = [];

        if (!data.client?.name) errors.push('Nome cliente');
        if (!data.client?.lastname) errors.push('Cognome cliente');
        if (!data.client?.address) errors.push('Indirizzo cliente');
        if (!data.client?.dateOfBirth) errors.push('Data di nascita cliente');
        if (!data.client?.placeOfBirth) errors.push('Luogo di nascita cliente');
        if (!data.event?.date) errors.push('Data evento');
        if (!data.event?.location) errors.push('Location');

        const hasServices = data.event?.services?.some((s: any) => s.selected);
        const hasLegacyServices = data.event?.service?.some(
          (s: any) => s.checked,
        );
        if (!hasServices && !hasLegacyServices) {
          errors.push('Almeno un servizio selezionato');
        }

        if (errors.length > 0) {
          // setTimeout: il loader della GET fa swal.close() nello stesso ciclo
          setTimeout(() => {
            this.swal.listWarning(
              this.t('swal.missing_data'),
              errors,
              this.t('swal.complete_contract_fields'),
            );
          });
          return;
        }

        this.connection.getBlob(
          `events/${eventId}/contract-pdf/preview?lang=${lang}`,
          (blob: Blob) => onReady(blob, { signed: false, lang }),
          () =>
            this.swal.errorDialog('Errore', 'Generazione anteprima fallita'),
        );
      },
      (err: any) => console.error(err),
    );
  }

  /** Genera e SALVA il contratto lato backend (finalizza l'anteprima). */
  finalizeContratto(
    eventId: string,
    lang: string,
    signed: boolean,
    onSuccess?: (res?: any) => void,
    onError?: () => void,
  ) {
    this.connection.post(
      `events/${eventId}/contract-pdf`,
      { lang, signed },
      (res: any) => {
        this.swal.success(
          signed ? 'PDF firmato generato!' : 'Contratto generato!',
        );
        if (onSuccess) onSuccess(res);
      },
      (err: any) => {
        // Il 403 da abbonamento è già gestito dall'errorInterceptor: qui evito
        // solo il doppio dialog, ma resetto comunque lo stato del chiamante.
        if (err?.status !== 403) {
          this.swal.errorDialog('Errore', 'Salvataggio contratto fallito');
        }
        if (onError) onError();
      },
    );
  }

  /** Genera e salva il contratto senza anteprima (creazione evento, PDF firmato). */
  async generaEsalvaContratto(
    eventId: string,
    onSuccess?: (res?: any) => void,
    signed = false,
  ) {
    let lang = 'it';
    if (!signed) {
      const { value } = await this.swal.select(
        this.t('swal.contract_lang_title'),
        { it: '🇮🇹 Italiano', en: '🇬🇧 English' },
        this.t('swal.generate'),
        this.t('swal.contract_lang_text'),
      );
      if (!value) return;
      lang = value;
    }
    this.finalizeContratto(eventId, lang, signed, onSuccess);
  }

  // ── VERSIONI CONTRATTO ────────────────────────────────────────────────────

  getContractVersions(
    id: string,
    success: (data: any) => void,
    error?: (e: any) => void,
  ) {
    this.connection.get(
      `events/${id}/contract/versions`,
      success,
      error ?? (() => {}),
    );
  }

  scaricaContractVersion(eventId: string, versionFile: string) {
    const name = versionFile.split('/').pop() ?? versionFile;
    this.connection.getBlob(
      `events/${eventId}/contract/version/${encodeURIComponent(versionFile)}`,
      (blob: Blob) =>
        this.savePdf(blob, /\.pdf$/i.test(name) ? name : `${name}.pdf`),
      () => this.swal.errorDialog(this.t('swal.error'), this.t('swal.download_failed')),
    );
  }

  private savePdf(blob: Blob, filename: string) {
    const url = URL.createObjectURL(
      new Blob([blob], { type: 'application/pdf' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── SCARICA / VISUALIZZA CONTRATTO ────────────────────────────────────────

  scaricaContratto(eventId: string) {
    this.connection.getBlob(
      `events/download_contract/${eventId}`,
      (blob: Blob) => this.savePdf(blob, `contratto-${eventId}.pdf`),
      () => this.swal.errorDialog(this.t('swal.error'), this.t('swal.download_failed')),
    );
  }

  visualizzaContratto(eventId: string) {
    this.connection.getBlob(
      `events/download_contract/${eventId}`,
      (blob: Blob) => {
        window.open(
          URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })),
          '_blank',
        );
      },
      () => this.swal.errorDialog(this.t('swal.error'), this.t('swal.contract_open_failed')),
    );
  }

  // ── UPLOAD IMMAGINI ───────────────────────────────────────────────────────

  uploadImmagini(
    eventId: string,
    files: File[],
    onSuccess: (data: any) => void,
    onError: () => void,
  ) {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));
    this.connection.post(
      `events/${eventId}/images`,
      formData,
      (data: any) => onSuccess(data),
      onError,
    );
  }

  updateImmagine(
    eventId: string,
    key: string,
    payload: { name?: string; note?: string },
    onSuccess: (data: any) => void,
    onError: () => void,
  ) {
    this.connection.put(
      `events/${eventId}/image?key=${encodeURIComponent(key)}`,
      payload,
      (data: any) => onSuccess(data),
      onError,
    );
  }

  deleteImmagine(
    eventId: string,
    key: string,
    onSuccess: (data: any) => void,
    onError: () => void,
  ) {
    this.connection.delete(
      `events/${eventId}/image?key=${encodeURIComponent(key)}`,
      (data: any) => onSuccess(data),
      onError,
    );
  }

  // ── UPLOAD DOCUMENTI ──────────────────────────────────────────────────────

  uploadDocumenti(
    eventId: string,
    files: File[],
    onSuccess: (data: any) => void,
    onError: () => void,
  ) {
    const formData = new FormData();
    files.forEach((file) => formData.append('documents', file));
    this.connection.post(
      `events/${eventId}/documents`,
      formData,
      (data: any) => onSuccess(data),
      onError,
    );
  }

  updateDocumento(
    eventId: string,
    key: string,
    payload: { name?: string; note?: string },
    onSuccess: (data: any) => void,
    onError: () => void,
  ) {
    this.connection.put(
      `events/${eventId}/document?key=${encodeURIComponent(key)}`,
      payload,
      (data: any) => onSuccess(data),
      onError,
    );
  }

  deleteDocumento(
    eventId: string,
    key: string,
    onSuccess: (data: any) => void,
    onError: () => void,
  ) {
    this.connection.delete(
      `events/${eventId}/document?key=${encodeURIComponent(key)}`,
      (data: any) => onSuccess(data),
      onError,
    );
  }

  // ── CALCOLO SALDO ─────────────────────────────────────────────────────────

  computeTotal(event: any): number {
    return eventTotals(event).total;
  }

  calcolaSaldo(event: any): number {
    const total = this.computeTotal(event);
    const deposit = resolveDepositEuro(event?.financial?.deposit, total);
    return Math.max(0, total - deposit);
  }

  // ── PAGAMENTI ─────────────────────────────────────────────────────────────

  updateFinancial(
    id: string,
    body: {
      deposit?: {
        cost?: number;
        type?: 'fixed' | 'percent';
        paid?: boolean;
        note?: string;
        paid_at?: string | null;
        method?: string;
      };
      travel_fee?: { cost?: number; paid?: boolean; note?: string };
      balance?: {
        paid?: boolean;
        note?: string;
        paid_at?: string | null;
        method?: string;
        due_date?: string | null;
      };
    },
    onSuccess: () => void,
    onError: () => void,
  ): void {
    this.connection.put(`events/${id}/financial`, body, onSuccess, onError);
  }

  // ── PROFILO ───────────────────────────────────────────────────────────────

  getProfile(success: (data: any) => void, error?: () => void) {
    this.connection.get('auth/profile', success, error ?? (() => {}));
  }

  // ── INIT DEFAULTS ─────────────────────────────────────────────────────────

  private initDefaults(m: any) {
    if (!m.financial) m.financial = {};
    if (!m.financial.deposit) m.financial.deposit = { paid: false, cost: 0, type: 'fixed' };
    if (!m.financial.travel_fee) m.financial.travel_fee = { cost: 0 };
    if (!m.financial.balance) m.financial.balance = { paid: false, note: '' };
    if (!m.financial.discount) m.financial.discount = emptyDiscount();
    if (m.financial.total_cost == null) m.financial.total_cost = 0;
    if (!m.image) m.image = { gallery: [] };
    if (!m.image.gallery) m.image.gallery = [];
    // Normalizza foto legacy (array di sole key) in oggetti { key, name, note }
    m.image.gallery = m.image.gallery.map((g: any) =>
      typeof g === 'string' ? { key: g } : g,
    );
    if (!m.document) m.document = { gallery: [] };
    if (!m.event.note) m.event.note = { text: '' };
    if (!m.event.contract) m.event.contract = { generateContract: false };
    if (!m.event.test_note) m.event.test_note = { text: '' };
    if (!m.event.service) m.event.service = [];
    if (!m.contract_blocks) m.contract_blocks = [];
    if (!m.quote_request) m.quote_request = {};
  }

  inviteCollaborator(eventId: string, email: string) {
    const registrationUrl = `${window.location.origin}/registrazione`;
    this.connection.post(
      `events/${eventId}/invite`,
      { email },
      (res: any) => {
        if (res.success) {
          this.swal.success('Invito inviato!');
        } else if (res.message === 'Invito già inviato') {
          this.swal.error('Hai già inviato un invito a questo indirizzo.');
        } else {
          this.swal.dialog(
            'info',
            'Utente non trovato su Nyzed',
            `${email} non è ancora registrato su Nyzed.\n\nCondividi questo link per invitarlo a registrarsi:\n${registrationUrl}`,
          );
        }
      },
      (err: any) => {
        this.swal.error(err?.error?.message ?? 'Invito non inviato');
      },
    );
  }

  getCollaborations(success: (data: any) => void, error?: () => void) {
    this.connection.get('events/collaborations', success, error ?? (() => {}));
  }

  getTeamAccessMembers(success: (data: any[]) => void) {
    this.connection.get(
      '@auth/team/members',
      (res: any) => success(res.members ?? []),
      () => success([]),
    );
  }

  getMatrimoni(success: (data: any[]) => void, error?: () => void) {
    this.connection.get('events', success, error ?? (() => {}));
  }

  // ── PREVENTIVO ────────────────────────────────────────────────────────────

  generateQuoteLink(
    eventId: string,
    success: (data: { link: string; expires_at: string }) => void,
    error: (err?: any) => void,
    validity?: { days?: number; expires_at?: string },
  ) {
    this.connection.post(`events/${eventId}/quote-link`, validity ?? {}, success, error);
  }

  updateStatus(
    eventId: string,
    status: 'quote' | 'confirmed',
    success: () => void,
    error: () => void,
  ) {
    this.connection.put(`events/${eventId}/status`, { status }, success, error);
  }

  getOwnerInfo(eventId: string, success: (data: any) => void) {
    this.connection.get(`events/${eventId}/owner`, success, () => {});
  }

  deleteEvent(eventId: string, success: () => void, error: () => void) {
    this.connection.delete(`events/${eventId}`, success, error);
  }

  removeCollaborator(eventId: string, userId: string, success: () => void) {
    this.connection.delete(
      `events/${eventId}/collaborator/${userId}`,
      success,
      () => this.swal.errorDialog('Errore', 'Rimozione fallita'),
    );
  }

  resendInvite(eventId: string, userId: string) {
    this.connection.post(
      `events/${eventId}/collaborator/${userId}/resend`,
      {},
      () => this.swal.success('Invito reinviato!'),
      () => this.swal.errorDialog('Errore', 'Invio fallito'),
    );
  }

  generateSignatureLink(
    eventId: string,
    success: (data: any) => void,
    error?: (err?: any) => void,
  ) {
    this.connection.post(
      `events/${eventId}/signature-link`,
      {},
      success,
      error ?? (() => {}),
    );
  }
  async prepareBrideSignature(eventId: string): Promise<string | null> {
    try {
      const response = await fetch(
        `${environment.apiUrl}/events/${eventId}/bride-signature`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      );
      if (!response.ok) return null;
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }
}
